import { FunctionComponent, useState } from "react"
import LogoFull from "../../../components/logo-full"
import LoginInput from "./login-input"
import { Link } from "react-router-dom"
import { useAddNewUserDataMutation } from "../../../app/services/meseeknaApi"
import { useNavigate } from "react-router-dom"
import { RootState, useTypedSelector } from "../../../app/store"
import React from "react"
import BaseButton from "../../../components/Button/base-button"

type SignUpFormProps = object
type ErrorBlockProps = {
  message: string
}

const SignUpForm: FunctionComponent<SignUpFormProps> = () => {
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [passwordError, setPasswordError] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("")
  const [acknowledge, setAcknowledge] = useState<boolean>(false)
  const [agreeTerms, setAgreeTerms] = useState<boolean>(false)
  const [agreeTermsError, setAgreeTermsError] = useState<string>("")
  const [acknowledgeError, setAcknowledgeError] = useState<string>("")
  const [responseError, setResponseError] = useState<string>("")
  const [postAddUser] = useAddNewUserDataMutation()
  const navigate = useNavigate()

  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
  const isValidPassword = (password: string): boolean => {
    return password.length >= 5
  }

  const handleFieldChange = (field: string, value: string) => {
    switch (field) {
      case "email":
        setEmail(value)
        if (!value) {
          setEmailError("Email is required")
        } else if (!isValidEmail(value)) {
          setEmailError("Invalid email format")
        } else {
          setEmailError("")
        }
        break
      case "password":
        setPassword(value)
        if (!value) {
          setPasswordError("Password is required")
        } else if (!isValidPassword(value)) {
          setPasswordError("Password must contain at least five characters")
        } else {
          setPasswordError("")
        }
        break
      case "confirmPassword":
        setConfirmPassword(value)
        if (!value) {
          setConfirmPasswordError("Confirm Password is required")
        } else if (value !== password) {
          setConfirmPasswordError("Please enter a matching password")
        } else {
          setConfirmPasswordError("")
        }
        break
      default:
        break
    }
  }
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()

    // Performing form validation
    handleFieldChange("email", email)
    handleFieldChange("password", password)
    handleFieldChange("confirmPassword", confirmPassword)

    if (!agreeTerms || !acknowledge) {
      if (!agreeTerms) {
        setAgreeTermsError("Please accept Terms & Conditions.")
      } else {
        setAgreeTermsError("")
      }

      if (!acknowledge) {
        setAcknowledgeError("Please confirm your acknowledgment.")
      } else {
        setAcknowledgeError("")
      }

      return
    }

    setAgreeTermsError("")
    setAcknowledgeError("")

    const payload = {
      Terms: agreeTerms,
      acknowledge: acknowledge,
      email: email,
      firstName: "",
      organizationId: authStore?.organization?.id
        ? authStore?.organization?.id
        : "",
      password: password,
      role: authStore?.organization?.role ? authStore?.organization?.role : "",
      userName: "",
    }
    try {
      const response = await postAddUser({
        body: payload,
      })
      if (response && response !== undefined && "data" in response) {
        const responseData = response?.data
        if (
          "data" in responseData &&
          "code" in responseData &&
          responseData?.code === 200
        ) {
          navigate("/dashboard")
        } else if ("fail" == responseData.status) {
          const message: string | undefined = responseData?.message
          if (
            responseData?.code === 208 &&
            responseData !== undefined &&
            message !== undefined &&
            message === "Email not found"
          ) {
            setResponseError(message)
          } else if (
            message !== undefined &&
            message === "Account already exists, Please Login"
          ) {
            setResponseError(message)
          }
        }
      }
    } catch (error) {
      console.error("Mutation error", error)
    }
  }

  const ErrorBlock: React.FC<ErrorBlockProps> = ({ message }) => {
    return (
      <>
        {message && message == "Email not found" && <div>Email not found</div>}
        {message && message == "Account already exists, Please Login" && (
          <div className="text-red-500 text-sm">
            Account already exists, please&nbsp;
            <Link className="text-red-500 text-sm" to={"/login"}>
              login
            </Link>
          </div>
        )}
      </>
    )
  }

  return (
    <main className="w-[480px] flex flex-col items-center justify-start gap-[32px] text-left text-sm text-gray-900 font-text-md-regular1">
      <div className="self-stretch flex flex-col items-center justify-start gap-[24px] text-center font-text-md-regular">
        <LogoFull
          dimensions="/v3/assets/meseekna-logo@2x.png"
          logoWrapWidth="189px"
          logoWrapHeight="31.5px"
          image1Width="188.61px"
          image1Height="30.02px"
        />
        <div className="self-stretch flex flex-col items-start justify-start gap-[12px]">
          <h1 className="m-0 self-stretch relative text-2xl leading-[48px] font-semibold font-inherit">
            Create a new Candidate account
          </h1>
        </div>
      </div>
      <div className="self-stretch rounded-xl flex flex-col items-center justify-start gap-[24px] text-gray-700">
        <LoginInput
          id="email"
          inputTitleText="Email"
          inputPlaceholderText="Enter Email"
          required={true}
          defaultValue={email}
          error={emailError}
          setValue={(value) => handleFieldChange("email", value)}
          inputType="email"
        />
        <LoginInput
          id="password"
          inputTitleText="Password"
          inputPlaceholderText="Enter Password"
          required={true}
          defaultValue={password}
          error={passwordError}
          setValue={(value) => handleFieldChange("password", value)}
          inputType="password"
        />
        <LoginInput
          id="confirmPassword"
          inputTitleText="Confirm Password"
          inputPlaceholderText="Confirm Password"
          required={true}
          defaultValue={confirmPassword}
          error={confirmPasswordError}
          setValue={(value) => handleFieldChange("confirmPassword", value)}
          inputType="password"
        />
        <div className="w-full flex-1 flex flex-row items-start justify-start gap-[8px]">
          <div className="flex flex-row pt-0.5 px-0 pb-0 items-center justify-center">
            <input
              className="cursor-pointer relative rounded bg-base-white box-border w-4 h-4 overflow-hidden shrink-0 border-[1px] border-solid border-gray-300"
              type="checkbox"
              name="Terms"
              data-testid="Terms"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
            />
          </div>
          <div className="flex-1 flex flex-col items-start justify-start">
            <div className="self-stretch relative leading-[24px] text-gray-600 font-medium">
              I agree to the
              <Link to={"https://www.meseekna.com/terms-of-use"}>
                Terms & Conditions
              </Link>
              .
            </div>
            {agreeTermsError && (
              <div className="text-red-500 text-sm">{agreeTermsError}</div>
            )}
          </div>
        </div>
        <div className="w-full flex-1 flex flex-row items-start justify-start gap-[8px]">
          <div className="flex flex-row pt-0.5 px-0 pb-0 items-center justify-center">
            <input
              className="cursor-pointer relative rounded bg-base-white box-border w-4 h-4 overflow-hidden shrink-0 border-[1px] border-solid border-gray-300"
              type="checkbox"
              checked={acknowledge}
              onChange={(e) => setAcknowledge(e.target.checked)}
              data-testid="Acknowledge"
            />
          </div>
          <div className="flex-1 flex flex-col items-start justify-start">
            <div className="self-stretch relative leading-[24px] text-gray-600 font-medium">
              I acknowledge that this product contains proprietary information
              and methodology and agree under no circumstances to duplicate,
              reverse engineer, share, or distribute the product by screenshot,
              picture, or otherwise.
            </div>
            {acknowledgeError && (
              <div className="text-red-500 text-sm">{acknowledgeError}</div>
            )}
          </div>
        </div>
        <ErrorBlock message={responseError} />
        <BaseButton
          data-testid="create-account-button"
          variant="primary"
          onClick={(e) => {
            handleCreateAccount(e)
          }}>
          Create Account
        </BaseButton>
        <div className="relative font-semibold text-gray-600">Or</div>
        <div className="self-stretch flex flex-row items-baseline justify-center gap-[4px] text-primary-700">
          <div className="overflow-hidden flex flex-row items-center justify-center gap-[8px]">
            <div className="relative leading-[24px] text-gray-600">
              Already created an account? Click here to
            </div>
            <div className="relative leading-[20px] font-semibold">
              <Link to={"/login"}>Sign in</Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default SignUpForm
